// ---------------------------------------------------------------------------
// What changed while the cart waited.
//
// The whole reason this dialog exists is that the alternative is worse: a cart
// held at yesterday's price and rung up at today's, with nobody told. The
// cashier has to tick a box saying they have told the customer, and until they
// do, the restored cart does not go back on the till.
//
// The prices shown are today's, because those are the ones that will be
// charged. The held price is shown beside them so the cashier can say by how
// much, rather than "it is different now".
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { AlertTriangle, Ban, TrendingUp } from 'lucide-react';
import { Modal } from '../common/Modal';
import { useI18n } from '../../i18n/I18nProvider';
import { formatQuantity, formatUsd } from '../../utils/format';
import type { RestoreWarning } from '../../types';

export function RestoreWarningsDialog({
  open,
  warnings,
  heldTotalUsd,
  currentTotalUsd,
  onAccept,
  onCancel,
}: {
  open: boolean;
  warnings: RestoreWarning[];
  heldTotalUsd: string;
  currentTotalUsd: string;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const { t, locale } = useI18n();
  const [acknowledged, setAcknowledged] = useState(false);

  const nameOf = (warning: RestoreWarning) =>
    locale === 'ar' ? warning.nameAr : warning.nameEn;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={t.held.warningsTitle}
      description={t.held.warningsBody}
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!acknowledged}
            onClick={onAccept}
          >
            {t.common.continue}
          </button>
        </>
      }
    >
      <ul className="space-y-2">
        {warnings.map((warning, index) => (
          <li
            key={`${warning.productId}-${warning.kind}-${index}`}
            className="flex items-start gap-2.5 rounded-control border border-antique-gold/40 bg-antique-gold/10 p-3 text-sm text-ink"
          >
            {warning.kind === 'PRICE_CHANGED' ? (
              <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-antique-gold-deep" aria-hidden="true" />
            ) : warning.kind === 'UNAVAILABLE' ? (
              <Ban className="mt-0.5 h-4 w-4 shrink-0 text-burgundy-red" aria-hidden="true" />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-burgundy-red" aria-hidden="true" />
            )}

            <span>
              {warning.kind === 'PRICE_CHANGED'
                ? t.held.priceChanged
                    .replace('{name}', nameOf(warning))
                    .replace('{was}', formatUsd(warning.heldPriceUsd))
                    .replace('{now}', formatUsd(warning.currentPriceUsd))
                : warning.kind === 'UNAVAILABLE'
                  ? t.held.unavailable.replace('{name}', nameOf(warning))
                  : t.held.insufficient
                      .replace('{name}', nameOf(warning))
                      .replace('{available}', formatQuantity(warning.available))
                      .replace('{requested}', formatQuantity(warning.requested))}
            </span>
          </li>
        ))}
      </ul>

      {/* Both totals, side by side. This is the number the argument at the
          counter will be about. */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-control bg-surface-sunken p-3">
          <p className="text-xs text-ink-muted">{t.held.heldTotal}</p>
          <p className="numeric mt-0.5 text-base font-semibold text-ink-muted line-through">
            {formatUsd(heldTotalUsd)}
          </p>
        </div>
        <div className="rounded-control bg-butcher-green/10 p-3">
          <p className="text-xs text-ink-muted">{t.held.currentTotal}</p>
          <p className="numeric mt-0.5 text-base font-semibold text-butcher-green">
            {formatUsd(currentTotalUsd)}
          </p>
        </div>
      </div>

      <label className="mt-4 flex items-start gap-2.5 text-sm text-ink">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
          className="mt-0.5 h-5 w-5 rounded border-line-strong text-butcher-green focus:ring-antique-gold"
        />
        <span>
          {t.held.acknowledge}
          <span className="mt-0.5 block text-xs text-ink-muted">{t.held.acknowledgeHint}</span>
        </span>
      </label>
    </Modal>
  );
}
